import { FC, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Popover } from '@mui/material';
import {
  AddOutlined,
  BedtimeOutlined,
  CalendarMonthOutlined,
  TaskAltOutlined,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import {
  quickActionSleepModalAtom,
  quickActionTaskCreateAtom,
  selectedDateAtom,
  useCreateEventMenuContext,
} from '../../data-access';
import './SideNavigation.scss';

const TEAM_BUTTON_SELECTOR = '.side-navigation button[aria-label="Team"]';
const FAB_SIZE = 32;

const SideNavigationAddFab: FC = () => {
  const { t } = useTranslation();
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const [fabPosition, setFabPosition] = useState<{ top: number; left: number } | null>(null);
  const addMenuAnchorRef = useRef<HTMLButtonElement>(null);
  const { handleSelectNewEvent } = useCreateEventMenuContext();
  const selectedDate = useRecoilValue(selectedDateAtom);
  const setIsSleepModalOpen = useSetRecoilState(quickActionSleepModalAtom);
  const setIsCreatingTask = useSetRecoilState(quickActionTaskCreateAtom);

  const addMenuId = isAddMenuOpen ? 'side-navigation-add-menu' : undefined;

  useEffect(() => {
    const updateFabPosition = () => {
      const teamButton = document.querySelector<HTMLButtonElement>(TEAM_BUTTON_SELECTOR);
      if (!teamButton) {
        setFabPosition(null);
        return;
      }

      const rect = teamButton.getBoundingClientRect();
      setFabPosition({
        top: rect.bottom + 16,
        left: rect.left + rect.width / 2 - FAB_SIZE / 2,
      });
    };

    const sideNavigation = document.querySelector('.side-navigation');

    updateFabPosition();
    window.addEventListener('resize', updateFabPosition);
    sideNavigation?.addEventListener('scroll', updateFabPosition);

    const resizeObserver =
      sideNavigation && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(updateFabPosition)
        : null;
    resizeObserver?.observe(sideNavigation);

    return () => {
      window.removeEventListener('resize', updateFabPosition);
      sideNavigation?.removeEventListener('scroll', updateFabPosition);
      resizeObserver?.disconnect();
    };
  }, []);

  const handleCloseAddMenu = () => {
    setIsAddMenuOpen(false);
  };

  const handleToggleAddMenu = () => {
    setIsAddMenuOpen((prev) => !prev);
  };

  const handleAddEvent = () => {
    const now = dayjs();
    const start = selectedDate.hour(now.hour()).minute(now.minute()).second(0).millisecond(0);
    const end = start.add(30, 'minute');

    handleSelectNewEvent({
      start: start.toISOString(),
      end: end.toISOString(),
    });
    handleCloseAddMenu();
  };

  const handleAddTask = () => {
    setIsCreatingTask(true);
    handleCloseAddMenu();
  };

  const handleEnterSleepData = () => {
    setIsSleepModalOpen(true);
    handleCloseAddMenu();
  };

  if (!fabPosition) {
    return null;
  }

  return createPortal(
    <>
      <button
        ref={addMenuAnchorRef}
        type="button"
        className={`side-navigation__fab ${isAddMenuOpen ? 'side-navigation__fab--open' : ''}`}
        style={{
          position: 'fixed',
          top: fabPosition.top,
          left: fabPosition.left,
          zIndex: 1200,
        }}
        aria-label={t('sideNavigation.add')}
        aria-controls={addMenuId}
        aria-haspopup="menu"
        aria-expanded={isAddMenuOpen}
        onClick={handleToggleAddMenu}
      >
        <AddOutlined className="side-navigation__fab-icon" />
      </button>
      <Popover
        id={addMenuId}
        anchorEl={addMenuAnchorRef.current}
        open={isAddMenuOpen}
        onClose={handleCloseAddMenu}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        className="side-navigation__add-menu"
      >
        <div className="side-navigation__contact-menu-list" role="menu">
          <button
            type="button"
            role="menuitem"
            className="side-navigation__contact-menu-item"
            onClick={handleAddEvent}
          >
            <CalendarMonthOutlined
              className="side-navigation__contact-menu-item-icon"
              aria-hidden
            />
            {t('sideNavigation.addMenu.addEvent')}
          </button>
          <button
            type="button"
            role="menuitem"
            className="side-navigation__contact-menu-item"
            onClick={handleAddTask}
          >
            <TaskAltOutlined className="side-navigation__contact-menu-item-icon" aria-hidden />
            {t('sideNavigation.addMenu.addTask')}
          </button>
          <button
            type="button"
            role="menuitem"
            className="side-navigation__contact-menu-item"
            onClick={handleEnterSleepData}
          >
            <BedtimeOutlined className="side-navigation__contact-menu-item-icon" aria-hidden />
            {t('sideNavigation.addMenu.enterSleepData')}
          </button>
        </div>
      </Popover>
    </>,
    document.body
  );
};

export default SideNavigationAddFab;
